import { BLANK_DISPLAY, formatValue } from './units.js';
export const TARGET_DIRECTIONS = ['AT_LEAST', 'AT_MOST'];
export const TARGET_PERIOD_TYPES = ['ANNUAL', 'CUSTOM'];
/**
 * The inclusive Sunday bounds of a target's period, or `null` when they cannot be resolved: an annual year without
 * reporting weeks, a custom week that does not exist, or a custom From after its To.
 */
export const resolveTargetBounds = (period, weeks) => {
    if (period.periodType === 'ANNUAL') {
        const prefix = `${period.year}-`;
        const dates = weeks
            .map((week) => week.weekDate)
            .filter((date) => date.startsWith(prefix))
            .sort();
        return dates.length === 0 ? null : { from: dates[0], to: dates[dates.length - 1] };
    }
    const start = weeks.find((week) => week.id === period.startWeekId);
    const end = weeks.find((week) => week.id === period.endWeekId);
    if (!start || !end || start.weekDate > end.weekDate) {
        return null;
    }
    return { from: start.weekDate, to: end.weekDate };
};
/** Two inclusive date spans share at least one day. */
export const periodsOverlap = (a, b) => a.from <= b.to && b.from <= a.to;
/** True when the span covers `date` (inclusive). */
export const coversDate = (bounds, date) => bounds.from <= date && date <= bounds.to;
/** The first target whose bounds cover `weekDate` (active targets of one KPI never overlap, so there is at most one). */
export const applicableTarget = (targets, weekDate) => targets.find((target) => coversDate({ from: target.fromWeekDate, to: target.toWeekDate }, weekDate)) ?? null;
/** A target links only to KPIs of the same unit — and, for Currency, the same currency. Text never has a target. */
export const targetKpiCompatible = (target, kpi) => target.unit !== 'TEXT' && target.unit === kpi.unit && (target.unit !== 'CURRENCY' || target.currency === kpi.currency);
/** A target value must be a finite number strictly greater than zero. */
export const isValidTargetValue = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0;
/**
 * Completion in percent, or `null` when there is nothing to compare (blank or non-finite actual, a target that is
 * not a positive finite number).
 */
export const computeCompletion = (actual, target, direction) => {
    if (typeof actual !== 'number' || !Number.isFinite(actual) || !isValidTargetValue(target)) {
        return null;
    }
    if (direction === 'AT_LEAST') {
        return (actual / target) * 100;
    }
    // actual > target > 0 here, so the division is safe.
    return actual <= target ? 100 : (target / actual) * 100;
};
/** `87.5%`, `—` for a blank. */
export const formatCompletion = (completion) => (completion === null ? BLANK_DISPLAY : formatValue('PERCENTAGE', completion));
/**
 * Which range figure a target is compared with: a cumulative balance by its Latest value; a count or an amount
 * (Number, Currency) by its Total; a rate, ratio or duration (Percentage, Ratio, Decimal, Days, Hours) by its Average.
 */
export const completionBasis = (kpi) => {
    if (kpi.valueType === 'CUMULATIVE') {
        return 'LATEST';
    }
    return kpi.unit === 'NUMBER' || kpi.unit === 'CURRENCY' ? 'TOTAL' : 'AVERAGE';
};
/** The range figure picked by `completionBasis`. */
export const completionActual = (summary, basis) => {
    switch (basis) {
        case 'TOTAL':
            return summary.total;
        case 'AVERAGE':
            return summary.average;
        case 'LATEST':
            return summary.latest;
    }
};
//# sourceMappingURL=completion.js.map